import * as React from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  LinearProgress,
  Stack,
  Typography,
} from "@mui/material";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import ScheduleIcon from "@mui/icons-material/Schedule";
import PlaceIcon from "@mui/icons-material/Place";

const SKILL_LEVEL_LABELS = {
  A: "A",
  A_B: "A/B",
  B: "B",
  B_C: "B/C",
  C_BEGINNER: "C/Beginner",
  ALL_WELCOME: "All are Welcome",
};

function formatGameDate(startsAt) {
  if (!startsAt) return "";
  const d = new Date(startsAt);
  return d.toLocaleDateString("en-US", {
    weekday: "long",
    year: "numeric",
    month: "long",
    day: "numeric",
  });
}

function formatGameTime(startsAt) {
  if (!startsAt) return "";
  const d = new Date(startsAt);
  return d.toLocaleTimeString("en-US", {
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  });
}

function formatCost(cents) {
  if (cents == null || cents === 0) return "Free";
  return `$${(cents / 100).toFixed(0)}`;
}

/**
 * Reusable game card showing date, time, location, organizer, game length,
 * skill level, player/goalie cost, skater and goalie spot availability, and actions.
 *
 * @param {Object} game - Game payload from API (search games or similar)
 * @param {string} game.id
 * @param {string} game.startsAt - ISO date string
 * @param {number} game.durationMinutes
 * @param {string} game.skillLevel - e.g. A, A_B, ALL_WELCOME
 * @param {string} [game.arenaName]
 * @param {Object} [game.rink] - { id, name, address, rinkPhone }
 * @param {number} [game.maxSkaterSpots]
 * @param {number} [game.maxGoalieSpots]
 * @param {number} [game.skaterSpotsFilled]
 * @param {number} [game.goalieSpotsFilled]
 * @param {Object} game.league
 * @param {Function} [onViewRoster] - (game) => void
 * @param {Function} [onSignUp] - (game) => void
 */
export default function GameCard({ game, onViewRoster, onSignUp }) {
  const league = game?.league ?? game?.organization ?? {};
  const superAdmin = league.superAdmin;
  const organizerName =
    superAdmin?.firstName != null && superAdmin?.lastName != null
      ? `${superAdmin.firstName} ${superAdmin.lastName}`
      : "—";
  const locationName =
    game?.arenaName || game?.rink?.name || league.name || "—";
  const locationAddress = game?.rink?.address;
  const cityState =
    league.city != null && league.state != null ? `${league.city}, ${league.state}` : "";
  const skillLabel = SKILL_LEVEL_LABELS[game?.skillLevel] ?? game?.skillLevel ?? "—";
  const playerCost = formatCost(league.playerCost);
  const goalieCost = formatCost(league.goalieCost);
  const costDisplay =
    goalieCost === "Free" ? `${playerCost} (Goalies: Free)` : `${playerCost} / Goalies: ${goalieCost}`;

  const maxSkater = game?.maxSkaterSpots ?? 0;
  const maxGoalie = game?.maxGoalieSpots ?? 0;
  const filledSkater = game?.skaterSpotsFilled ?? 0;
  const filledGoalie = game?.goalieSpotsFilled ?? 0;
  const availableSkater = Math.max(0, maxSkater - filledSkater);
  const availableGoalie = Math.max(0, maxGoalie - filledGoalie);
  const skaterProgress = maxSkater > 0 ? filledSkater / maxSkater : 0;
  const goalieProgress = maxGoalie > 0 ? filledGoalie / maxGoalie : 0;

  return (
    <Card variant="outlined">
      <CardContent>
        <Stack spacing={2}>
          <Stack direction="row" justifyContent="space-between" alignItems="flex-start" flexWrap="wrap" gap={1}>
            <Stack direction="row" alignItems="center" spacing={1} sx={{ flex: 1, minWidth: 0 }}>
              <CalendarMonthIcon fontSize="small" color="action" />
              <Typography variant="body2" color="text.secondary">
                {formatGameDate(game?.startsAt)}
              </Typography>
            </Stack>
            <Stack direction="row" alignItems="center" spacing={1}>
              <ScheduleIcon fontSize="small" color="action" />
              <Typography variant="body2" color="text.secondary">
                {formatGameTime(game?.startsAt)}
              </Typography>
            </Stack>
          </Stack>

          <Typography variant="h6" component="h2">
            {locationName}
          </Typography>
          {(locationAddress || cityState) ? (
            <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap">
              <PlaceIcon fontSize="small" color="action" />
              <Typography variant="body2" color="text.secondary">
                {locationAddress ? `${locationAddress}${cityState ? ` · ${cityState}` : ""}` : cityState}
              </Typography>
            </Stack>
          ) : null}

          <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
            <Box sx={{ flex: 1 }}>
              <Typography variant="caption" color="text.secondary" display="block">
                Organized by
              </Typography>
              <Typography variant="body2">{organizerName}</Typography>
            </Box>
            <Box sx={{ flex: 1 }}>
              <Typography variant="caption" color="text.secondary" display="block">
                Game length
              </Typography>
              <Typography variant="body2">
                {game?.durationMinutes != null ? `${game.durationMinutes} Minutes` : "—"}
              </Typography>
            </Box>
            <Box sx={{ flex: 1 }}>
              <Typography variant="caption" color="text.secondary" display="block">
                Skill Level
              </Typography>
              <Typography variant="body2">{skillLabel}</Typography>
            </Box>
            <Box sx={{ flex: 1 }}>
              <Typography variant="caption" color="text.secondary" display="block">
                Cost
              </Typography>
              <Typography variant="body2">{costDisplay}</Typography>
            </Box>
          </Stack>

          {maxSkater > 0 && (
            <Box>
              <Typography variant="body2" color="text.secondary">
                Skater spots: {filledSkater} of {maxSkater} filled
              </Typography>
              <LinearProgress
                variant="determinate"
                value={Math.min(100, skaterProgress * 100)}
                color={availableSkater === 0 ? "error" : "primary"}
                sx={{ mt: 0.5, height: 8, borderRadius: 1 }}
              />
              <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: "block" }}>
                {availableSkater} available
              </Typography>
            </Box>
          )}
          {maxGoalie > 0 && (
            <Box>
              <Typography variant="body2" color="text.secondary">
                Goalie spots: {filledGoalie} of {maxGoalie} filled
              </Typography>
              <LinearProgress
                variant="determinate"
                value={Math.min(100, goalieProgress * 100)}
                color={availableGoalie === 0 ? "error" : "warning"}
                sx={{ mt: 0.5, height: 8, borderRadius: 1 }}
              />
              <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: "block" }}>
                {availableGoalie} available
              </Typography>
            </Box>
          )}

          <Stack direction="row" spacing={2} sx={{ pt: 1 }}>
            {onViewRoster && (
              <Button variant="outlined" onClick={() => onViewRoster(game)}>
                View Roster
              </Button>
            )}
            {onSignUp && (
              <Button variant="contained" color="success" onClick={() => onSignUp(game)}>
                Sign Up Now
              </Button>
            )}
          </Stack>
        </Stack>
      </CardContent>
    </Card>
  );
}

export { SKILL_LEVEL_LABELS, formatGameDate, formatGameTime, formatCost };
